import React, { useMemo, useState, useCallback } from "react";
import { Throughput } from "../../../types/outbox.types";
import { LANE_ORDER } from "./LaneBadge";
import { format, parseISO } from "date-fns";

interface ThroughputChartProps {
  data: Throughput;
}

const LANE_BAR_COLORS: Record<string, string> = {
  fast: "bg-green-400",
  io: "bg-blue-400",
  llm: "bg-purple-400",
  default: "bg-gray-400",
};

const LANE_BAR_COLORS_HOVER: Record<string, string> = {
  fast: "bg-green-500",
  io: "bg-blue-500",
  llm: "bg-purple-500",
  default: "bg-gray-500",
};

const LANE_LEGEND_COLORS: Record<string, string> = {
  fast: "bg-green-400",
  io: "bg-blue-400",
  llm: "bg-purple-400",
  default: "bg-gray-400",
};

const formatTime = (isoString: string): string => {
  try {
    const date = parseISO(isoString);
    return format(date, "HH:mm");
  } catch {
    return isoString;
  }
};

export const ThroughputChart: React.FC<ThroughputChartProps> = ({ data }) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const [hiddenLanes, setHiddenLanes] = useState<Set<string>>(new Set());

  const toggleLane = useCallback((lane: string) => {
    setHiddenLanes((prev) => {
      const next = new Set(prev);
      if (next.has(lane)) {
        next.delete(lane);
      } else {
        next.add(lane);
      }
      return next;
    });
  }, []);

  // Discover which lanes actually have data
  const activeLanes = useMemo(() => {
    const laneSet = new Set<string>();
    for (const bucket of data.buckets) {
      if (bucket.perLane) {
        for (const lane of Object.keys(bucket.perLane)) {
          laneSet.add(lane);
        }
      }
    }
    return LANE_ORDER.filter((l) => laneSet.has(l));
  }, [data]);

  const visibleLanes = useMemo(
    () => activeLanes.filter((l) => !hiddenLanes.has(l)),
    [activeLanes, hiddenLanes],
  );

  const { maxCount, bars } = useMemo(() => {
    if (!data.buckets.length) return { maxCount: 0, bars: [] };

    const calculatedBars = data.buckets.map((bucket) => {
      const visibleCount = visibleLanes.reduce(
        (sum, lane) => sum + (bucket.perLane?.[lane] || 0),
        0,
      );
      return { ...bucket, visibleCount };
    });

    const max = Math.max(...calculatedBars.map((b) => b.visibleCount), 1);

    return {
      maxCount: max,
      bars: calculatedBars.map((b) => ({
        ...b,
        percentage: max > 0 ? (b.visibleCount / max) * 100 : 0,
      })),
    };
  }, [data, visibleLanes]);

  if (!data.buckets.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Przepustowość eventów (5-min buckets)
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla ostatnich 24h
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="outbox-throughput-chart"
      role="figure"
      aria-label="Wykres przepustowości eventów"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-neutral-900">
          Przepustowość eventów (5-min buckets)
        </h3>

        {/* Lane legend */}
        <div className="flex gap-3">
          {activeLanes.map((lane) => {
            const isHidden = hiddenLanes.has(lane);
            return (
              <button
                key={lane}
                onClick={() => toggleLane(lane)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors ${
                  isHidden
                    ? "opacity-40 line-through text-neutral-400"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
                aria-pressed={!isHidden}
                aria-label={`${isHidden ? "Pokaż" : "Ukryj"} lane ${lane}`}
              >
                <span
                  className={`inline-block w-3 h-3 rounded-sm ${LANE_LEGEND_COLORS[lane] ?? LANE_LEGEND_COLORS.default}`}
                />
                {lane}
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative flex gap-1 h-48">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-neutral-500 pr-2 py-0.5">
          <span>{maxCount}</span>
          <span>{Math.floor(maxCount / 2)}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative h-full">
          {/* Bars */}
          <div className="flex items-end gap-0 h-full">
            {bars.map((bar, index) => (
              <div
                key={bar.bucket}
                className="flex-1 h-full flex flex-col justify-end items-center relative"
                data-testid={`bar-${index}`}
                onMouseEnter={() => setActiveTooltip(index)}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                {/* Tooltip */}
                <div
                  className={`absolute bottom-full mb-2 z-10 transition-opacity ${
                    activeTooltip === index
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="bg-neutral-900 text-white text-xs rounded px-2 py-1.5 whitespace-nowrap">
                    <div className="font-medium mb-1">
                      {formatTime(bar.bucket)}
                    </div>
                    {visibleLanes.map((lane) => {
                      const count = bar.perLane?.[lane] || 0;
                      if (count === 0) return null;
                      return (
                        <div key={lane} className="flex items-center gap-1.5">
                          <span
                            className={`inline-block w-2 h-2 rounded-sm ${LANE_LEGEND_COLORS[lane] ?? LANE_LEGEND_COLORS.default}`}
                          />
                          {lane}: {count}
                        </div>
                      );
                    })}
                    <div className="border-t border-neutral-700 mt-1 pt-1 font-medium">
                      Total: {bar.visibleCount}
                    </div>
                  </div>
                </div>

                {/* Stacked bar */}
                <div
                  tabIndex={0}
                  role="img"
                  aria-label={`${formatTime(bar.bucket)}: ${bar.visibleCount} eventów`}
                  className="w-full flex flex-col-reverse cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                  style={{
                    height: `${bar.visibleCount > 0 ? Math.max(bar.percentage, 1) : 0}%`,
                  }}
                  onFocus={() => setActiveTooltip(index)}
                  onBlur={() => setActiveTooltip(null)}
                  onTouchStart={() => setActiveTooltip(index)}
                >
                  {visibleLanes.map((lane) => {
                    const count = bar.perLane?.[lane] || 0;
                    if (count === 0 || bar.visibleCount === 0) return null;
                    const segmentPercent = (count / bar.visibleCount) * 100;
                    return (
                      <div
                        key={lane}
                        className={`w-full ${
                          activeTooltip === index
                            ? (LANE_BAR_COLORS_HOVER[lane] ??
                              LANE_BAR_COLORS_HOVER.default)
                            : (LANE_BAR_COLORS[lane] ??
                              LANE_BAR_COLORS.default)
                        } transition-colors first:rounded-b last:rounded-t`}
                        style={{ height: `${segmentPercent}%`, minHeight: 1 }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-neutral-500 ml-12">
        {bars.length > 0 && (
          <>
            <span>{formatTime(bars[0].bucket)}</span>
            {bars.length > 2 && (
              <span>
                {formatTime(bars[Math.floor(bars.length / 2)].bucket)}
              </span>
            )}
            {bars.length > 1 && (
              <span>{formatTime(bars[bars.length - 1].bucket)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ThroughputChart;
